import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import * as storage from '../utils/localStorage';
import { usePersona } from '../hooks/usePersona';
import { useConversations } from '../hooks/useConversations';
import { useChat } from '../hooks/useChat';
import { useSystemUpdates } from '../hooks/useSystemUpdates';
import { useEscalations } from '../hooks/useEscalations';
import { useLiveChat } from '../hooks/useLiveChat';
import {
  AIMessage,
  ClickableSuggestion,
  ChatTheme,
  ConversationThread,
  PendingConfirmation,
  FileAttachment,
} from './types';
import { ChatInputRef } from './ChatInput';
import { PersonaType, PersonaAssistantConfig } from '../config/personaConfig';

interface AIAssistantContextValue {
  // Auth
  user: any;
  currentCompanyId: number | null | undefined;
  canAdminSystem: boolean;
  canReviewCompliance: boolean;

  // UI state
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isMinimized: boolean;
  setIsMinimized: React.Dispatch<React.SetStateAction<boolean>>;
  isDarkMode: boolean;
  setIsDarkMode: React.Dispatch<React.SetStateAction<boolean>>;
  theme: ChatTheme;
  sidebarView: 'conversations' | 'escalations' | 'livechat';
  setSidebarView: React.Dispatch<React.SetStateAction<'conversations' | 'escalations' | 'livechat'>>;

  // Persona
  persona: PersonaType;
  personaConfig: PersonaAssistantConfig;

  // Shared message state
  messages: AIMessage[];
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  welcomeMessage: string;
  setWelcomeMessage: React.Dispatch<React.SetStateAction<string>>;
  suggestions: ClickableSuggestion[];
  setSuggestions: React.Dispatch<React.SetStateAction<ClickableSuggestion[]>>;

  // Conversations
  conversationId: string | null;
  conversations: ConversationThread[];
  isConversationsLoading: boolean;
  hasMoreConversations: boolean;
  loadMoreConversations: () => Promise<void>;
  handleSelectConversation: (conversation: ConversationThread) => Promise<void>;
  handleNewChat: () => Promise<void>;
  handleDeleteConversation: (convId: string, e: React.MouseEvent) => Promise<void>;
  handleExportConversation: (conv: ConversationThread, e: React.MouseEvent) => void;

  // Chat
  inputValue: string;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  isLoading: boolean;
  isStreaming: boolean;
  statusMessage: string;
  pendingConfirmation: PendingConfirmation | null;
  attachments: FileAttachment[];
  setAttachments: React.Dispatch<React.SetStateAction<FileAttachment[]>>;
  chatInputRef: React.RefObject<ChatInputRef>;
  handleSendMessage: (overrideMessage?: string) => Promise<void>;
  handleConfirmAction: () => Promise<void>;
  handleCancelConfirmation: () => void;
  handleSuggestionClick: (suggestion: ClickableSuggestion) => void;
  handleEditMessage: (messageIndex: number) => void;
  handleStopProcessing: () => void;

  // System updates
  hasUnseenUpdates: boolean;
  hasUnseenChangeNotifications: boolean;

  // Escalations (admin only)
  escalations: any[];
  escalationsTotal: number;
  escalationsLoading: boolean;
  fetchEscalations: (status?: string) => Promise<void>;
  claimEscalation: (id: string) => Promise<void>;
  resolveEscalation: (id: string) => Promise<void>;
  transferEscalation: (id: string, target: { to_agent_id?: number; to_department?: string }, note?: string) => Promise<void>;
  addEscalationNote: (id: string, note: string) => Promise<void>;

  // Live chat (admin only)
  liveChats: any[];
  selectedChat: string | null;
  setSelectedChat: React.Dispatch<React.SetStateAction<string | null>>;
  chatMessages: any[];
  setChatMessages: React.Dispatch<React.SetStateAction<any[]>>;
  chatInput: string;
  setChatInput: React.Dispatch<React.SetStateAction<string>>;
  chatSending: boolean;
  chatEndRef: React.RefObject<HTMLDivElement>;
  fetchChatMessages: (convId: string) => Promise<void>;
  acceptLiveChat: (convId: string) => Promise<void>;
  sendChatMessage: () => Promise<void>;
  resolveLiveChat: (convId: string) => Promise<void>;
}

const AIAssistantContext = createContext<AIAssistantContextValue | null>(null);

export function useAIAssistant(): AIAssistantContextValue {
  const ctx = useContext(AIAssistantContext);
  if (!ctx) {
    throw new Error('useAIAssistant must be used within <AIAssistantProvider>');
  }
  return ctx;
}

interface AIAssistantProviderProps {
  companyId?: number;
  children: React.ReactNode;
}

export function AIAssistantProvider({ companyId, children }: AIAssistantProviderProps) {
  const { user } = useAuth();

  // UI state
  const [isOpen, setIsOpen] = useState(() => storage.getAssistantOpen());
  const [isMinimized, setIsMinimized] = useState(() => storage.getAssistantMinimized());
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [sidebarView, setSidebarView] = useState<'conversations' | 'escalations' | 'livechat'>('conversations');

  // Shared message state (owned by provider, passed to hooks)
  const [messages, setMessages] = useState<AIMessage[]>([]);
  const [welcomeMessage, setWelcomeMessage] = useState<string>('');
  const [suggestions, setSuggestions] = useState<ClickableSuggestion[]>([]);

  const currentCompanyId = companyId || user?.company_id;
  const currentPartnerId = (user as any)?.partner_id ?? null;
  const permissions = user?.permissions ?? [];
  const canAdminSystem = permissions.includes('system.config');
  const canReviewCompliance = permissions.includes('compliance.review');

  const userName = user?.first_name || user?.email?.split('@')[0] || 'there';

  // Hooks
  const personaHook = usePersona({ permissions, companyId: currentCompanyId, partnerId: currentPartnerId, userName });

  const conversationsHook = useConversations({
    userId: user?.id,
    companyId: currentCompanyId,
    generateWelcomeMessage: personaHook.generateWelcomeMessage,
    generateSuggestions: personaHook.generateSuggestions,
    messages,
    setMessages,
    welcomeMessage,
    setWelcomeMessage,
    suggestions,
    setSuggestions,
  });

  const chatHook = useChat({
    conversationId: conversationsHook.conversationId,
    currentCompanyId,
    ensureConversationId: conversationsHook.ensureConversationId,
    setConversations: conversationsHook.setConversations,
    generateSmartTitle: conversationsHook.generateSmartTitle,
    messages,
    setMessages,
    setSuggestions,
  });

  const systemUpdatesHook = useSystemUpdates({
    userId: user?.id,
    isOpen,
    canAdminSystem,
    canReviewCompliance,
    setMessages,
  });

  const escalationsHook = useEscalations({
    canAdminSystem,
    sidebarView,
    userId: user?.id,
  });

  const liveChatHook = useLiveChat({
    canAdminSystem,
    sidebarView,
  });

  // Theme
  const theme: ChatTheme = useMemo(() => ({
    isDark: isDarkMode,
    colors: {
      background: isDarkMode ? 'bg-gray-900' : 'bg-white',
      surface: isDarkMode ? 'bg-gray-800' : 'bg-gray-100',
      text: isDarkMode ? 'text-white' : 'text-gray-900',
      textSecondary: isDarkMode ? 'text-gray-400' : 'text-gray-600',
      border: isDarkMode ? 'border-gray-700' : 'border-gray-200',
      userMessage: 'bg-blue-600 text-white',
      assistantMessage: isDarkMode ? 'bg-gray-800 border border-gray-700 text-gray-200' : 'bg-gray-100 border border-gray-200 text-gray-800',
      input: isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-200 text-gray-900'
    }
  }), [isDarkMode]);

  // Persist UI state
  useEffect(() => { storage.setAssistantOpen(isOpen); }, [isOpen]);
  useEffect(() => { storage.setAssistantMinimized(isMinimized); }, [isMinimized]);
  useEffect(() => { storage.setActiveConversation(conversationsHook.conversationId); }, [conversationsHook.conversationId]);

  // Load conversation history
  useEffect(() => {
    if (user?.id) {
      conversationsHook.loadConversationHistory().catch(() => {
        setTimeout(() => {
          if (user?.id) conversationsHook.loadConversationHistory().catch(() => {});
        }, 2000);
      });
    }
  }, [user?.id, currentCompanyId]);

  // Generate welcome message and suggestions — always show for the current conversation
  useEffect(() => {
    if (!welcomeMessage && !conversationsHook.isConversationsLoading) {
      setWelcomeMessage(personaHook.generateWelcomeMessage());
    }
  }, [welcomeMessage, conversationsHook.isConversationsLoading]);

  useEffect(() => {
    if (welcomeMessage && suggestions.length === 0) {
      setSuggestions(personaHook.generateSuggestions());
    }
  }, [conversationsHook.conversationId, welcomeMessage, suggestions.length]);

  // Reload conversation history when widget opens
  useEffect(() => {
    if (isOpen && user?.id && !conversationsHook.isConversationsLoading) {
      conversationsHook.loadConversationHistory().catch(() => {});
    }
  }, [isOpen]);

  const value: AIAssistantContextValue = {
    user,
    currentCompanyId,
    canAdminSystem,
    canReviewCompliance,

    isOpen,
    setIsOpen,
    isMinimized,
    setIsMinimized,
    isDarkMode,
    setIsDarkMode,
    theme,
    sidebarView,
    setSidebarView,

    persona: personaHook.persona,
    personaConfig: personaHook.personaConfig,

    messages,
    setMessages,
    welcomeMessage,
    setWelcomeMessage,
    suggestions,
    setSuggestions,

    conversationId: conversationsHook.conversationId,
    conversations: conversationsHook.conversations,
    isConversationsLoading: conversationsHook.isConversationsLoading,
    hasMoreConversations: conversationsHook.hasMoreConversations,
    loadMoreConversations: conversationsHook.loadMoreConversations,
    handleSelectConversation: conversationsHook.handleSelectConversation,
    handleNewChat: conversationsHook.handleNewChat,
    handleDeleteConversation: conversationsHook.handleDeleteConversation,
    handleExportConversation: conversationsHook.handleExportConversation,

    inputValue: chatHook.inputValue,
    setInputValue: chatHook.setInputValue,
    isLoading: chatHook.isLoading,
    isStreaming: chatHook.isStreaming,
    statusMessage: chatHook.statusMessage,
    pendingConfirmation: chatHook.pendingConfirmation,
    attachments: chatHook.attachments,
    setAttachments: chatHook.setAttachments,
    chatInputRef: chatHook.chatInputRef,
    handleSendMessage: chatHook.handleSendMessage,
    handleConfirmAction: chatHook.handleConfirmAction,
    handleCancelConfirmation: chatHook.handleCancelConfirmation,
    handleSuggestionClick: chatHook.handleSuggestionClick,
    handleEditMessage: chatHook.handleEditMessage,
    handleStopProcessing: chatHook.handleStopProcessing,

    hasUnseenUpdates: systemUpdatesHook.hasUnseenUpdates,
    hasUnseenChangeNotifications: systemUpdatesHook.hasUnseenChangeNotifications,

    ...escalationsHook,
    ...liveChatHook,
  };

  return (
    <AIAssistantContext.Provider value={value}>
      {children}
    </AIAssistantContext.Provider>
  );
}
